import React, { ReactNode, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { has, isEmpty } from 'lodash';
import { Tooltip } from '@mui/material';
import { selectCard } from 'js/actions';
import DataSourceDetailActionsContainer from 'js/components/data_sources/cards/DataSourceDetailActionsContainer';
import { DataSourceDetailHeader } from 'js/components/data_sources/cards/DataSourceDetailHeader';
import TagsView from 'js/components/elements/tags/TagsView';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import DateUtils from 'js/lib/DateUtils';
import UIUtils from 'js/lib/UIUtils';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import DataCardList from 'new/elements/DataCardList';
import { mapStateToProps } from 'js/components/data_sources/cards/stateMapper';
import { AvailableFilters } from '../MacroInput/types/AvailableFilters';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import FlowTypeIcon from 'js/components/pipelines/cards/FlowTypeIcon';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { DetailsIconButton } from 'new/elements/Button';
import styles from './DataSourceTable.module.scss';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { useResourceHeaderTabs } from 'new/hooks/useResourceHeaderTabs';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  availableFilterTypes: string[];
  getSourceMeta: (id: number) => any;
  loadData: (page: number, sortCriteria: string, availableFilters: AvailableFilters) => void;
  view: SearchFilterViewsType;
  noContentView?: ReactNode;
};

const makeRowRenderer =
  ({ handleCardSelected, getData }) =>
  (index: any) => {
    const {
      createdAt,
      owner,
      cardData,
      readOnly,
      isManaged,
      aclQueryMode,
      sourceIcon,
      sourceName,
      setsCount,
      setsLabel,
      connectionOrVendorType,
    } = getData(index);
    const readOnlyLabel = shouldDisplayOwnershipLabel(aclQueryMode)
      ? ownershipLabel(cardData)
      : null;

    return (
      <DataCard
        className={styles.cardContainer}
        onClick={() => handleCardSelected(cardData)}
        rowsSlots={[
          <>
            <NameDescCell name={cardData.name} className={styles.nameCardCell} />,
            <DataCardCell rightCTAsCell className={styles.sourceCardCell}>
              <PipelineStatusBadge
                id={cardData.id}
                revertDirection
                status={cardData.status as Status}
                runtimeStatus={cardData?.runtime_status}
                ingestionMode={cardData.ingestion_mode}
                parentArchivalStatus={cardData?.archival_status}
              />
              <DetailsIconButton onClick={() => handleCardSelected(cardData)} />
              <DataSourceDetailActionsContainer
                data={cardData}
                showStatus={false}
                readOnly={readOnly}
                isManaged={isManaged}
                connectionOrVendorType={connectionOrVendorType}
                openDetailsModal={() => handleCardSelected(cardData)}
              />
            </DataCardCell>
          </>,
          <DataCardCell className={styles.sourceCardCell}>
            <Stack gap="16px" direction="row" alignItems="center" justifyContent="flex-end">
              <Tooltip title={cardData.name} arrow>
                <Stack className={styles.resourceInfo}>
                  <ConnectionTypeChip
                    icon={sourceIcon}
                    label={sourceName}
                    style={{ border: 'none', padding: 0 }}
                  />
                </Stack>
              </Tooltip>
              <ChipWithCopy tooltipTitle="Copy Source ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
              <FlowTypeIcon data={cardData} className={styles.flowTypeContainer} />
            </Stack>
          </DataCardCell>,
          <DataCardCell multiCol className={styles.sourceCardCell}>
            <div>
              <Typography variant="body3">Created on {createdAt}</Typography>
              {readOnlyLabel && (
                <div>
                  <Typography variant="caption">{readOnlyLabel}</Typography>
                  {!isOwner(cardData) && <Typography variant="body3">Owned by</Typography>}
                  {!isOwner(cardData) && (
                    <Typography variant="body3">{owner && owner.email}</Typography>
                  )}
                </div>
              )}
            </div>
            <div>
              <Typography variant="body3" color="primary">
                {setsCount}{' '}
              </Typography>
              <Typography variant="body3">{setsLabel}</Typography>
            </div>
          </DataCardCell>,
          !isEmpty(cardData.tags) && (
            <DataCardCell className={styles.sourceCardCell}>
              <TagsView data={cardData} resourceType={RESOURCE_TYPE_SOURCE} />
            </DataCardCell>
          ),
        ].filter(Boolean)}
      />
    );
  };

const DataSourcesTable = ({
  availableFilterTypes,
  view,
  urlId,
  isLoading,
  data,
  getSourceMeta,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useAppDispatch();
  const { aclQueryMode, selectedCardId } = useSelector((state: any) => state.app.listing);

  const selectedSource = data?.find((source) => source.id === selectedCardId);
  const detailViewTabs = (useResourceHeaderTabs(selectedSource, {
    resourceType: RESOURCE_TYPE_SOURCE,
    flow: null,
  }) || []) as any[];

  const handleCardSelected = useCallback((row) => {
    dispatch(selectCard(row.id));
  }, []);

  const getData = useListOldReduxContainer(
    data,
    mapStateToProps,
    (ownProps) => {
      const {
        connectionOrVendorType,
        cardData,
        summaryData,
        readOnly,
        isManaged,
        adminAccess,
        owner,
      } = ownProps;
      const createdAt = DateUtils.convertDateStringToFormat(cardData?.created_at);

      const setsCount = has(summaryData, 'data_sets') ? summaryData.data_sets : null;
      const setsLabel =
        setsCount !== null ? UIUtils.toPluralFormat('Nexset', setsCount, false) : null;
      const description = cardData?.description || ' ';

      const connectionInfo = getSourceMeta(cardData?.id);

      let sourceIcon;
      let sourceName;

      if (connectionInfo) {
        sourceIcon = connectionInfo.iconSm;
        sourceName = connectionInfo.name;
      } else {
        sourceName = connectionOrVendorType;
      }

      return {
        description,
        createdAt,
        owner,
        cardData,
        readOnly,
        isManaged,
        adminAccess,
        aclQueryMode,
        sourceIcon,
        sourceName,
        setsCount,
        setsLabel,
        connectionOrVendorType,
      };
    },
    [getSourceMeta],
  );

  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  return (
    <DataCardList
      data={data}
      view={view}
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      urlId={urlId}
      loading={isLoading}
      detailHeaderContent={
        <DataSourceDetailHeader
          {...getData(data.findIndex((item: any) => item.id === selectedCardId))}
        />
      }
      rowRenderer={makeRowRenderer({ handleCardSelected, getData })}
      getSourceMeta={getSourceMeta}
      noContentView={noContentView}
      loadData={loadData}
    />
  );
};

export default DataSourcesTable;
